import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { BehaviorSubject } from 'rxjs';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class PipelineOverviewWalkthroughService {
  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.PipelineOverview;

  public openPipelineDropdown$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private steps: Step[] = [
    {
      id: 'walkthrough-step-pipeline-overview-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_1.description_v2',
          ),
          imageUrl: walkthroughAssetsResolver('pipeline_overview.svg'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-pipeline-overview-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }],
        offset: { offsetX: 0, offsetY: -12 },
      },
    },
    {
      id: 'walkthrough-step-pipeline-overview-3',
      position: 3,

      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_3.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_3.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }],
        offset: { offsetX: 0, offsetY: -24 },
      },
    },
    {
      id: 'walkthrough-step-pipeline-overview-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-pipeline-overview-5',
      position: 5,
      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_5.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.pipeline_overview.step_5.cta_text',
          ),
          helpArticleKey: 'customizePipelineView',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  constructor(
    private walkthroughStepsService: WalkthroughStepsService,
    private router: Router,
    private i18n: I18nService,
    private gettingStartedFacade: GettingStartedFacade,
    private navigationHelperService: WalkthroughNavigationHelperService,
  ) {}

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, ['offers']);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.router.navigate(['offers']);
  }
}
